/**
 * Advertisement Label — regulatory header sitting above every widget surface.
 *
 * Partner publishers must be visibly separated from the widget so they are not
 * read as introducing brokers soliciting orders for prediction contracts. The
 * label is therefore unconditional: it shows on every surface (pill, preview,
 * card), in both floating and inline modes, at every viewport.
 *
 * It renders outside the surface chrome on a transparent background, so it sits
 * directly against the partner page rather than the widget's dark backplate.
 * That is why it uses `type-muted-foreground` (#5D656B) instead of the
 * surfaces' white-on-dark palette — the one token that reads on either.
 *
 * The rules are decorative; the word itself is the disclosure, so it stays in
 * the accessibility tree.
 */

import type { ComponentChildren, JSX } from "preact";
import { messages } from "../i18n";

export interface AdvertisementLabelProps {
  className?: string;
  /**
   * Rendered after the trailing hairline, sharing the row's flex gap. The
   * preview passes its close button here: the button already floated in the
   * band this label now occupies, so keeping it in the same row is what stops
   * the hairline running underneath it.
   */
  trailing?: ComponentChildren;
}

// Figma "Label/Small_Allcaps": 11px / 16px, weight 400, 0.03px tracking,
// uppercase. Stored sentence-case in the locale bundle and capitalized here,
// so translations aren't forced into caps that some scripts don't have.
const labelTextClasses = [
  "pw:text-[0.6875rem] pw:font-normal pw:leading-4 pw:tracking-[0.003em]",
  "pw:uppercase pw:text-center pw:whitespace-nowrap",
  "pw:text-type-muted-foreground",
].join(" ");

// The band is a fixed 24px tall and sits flush on the surface below it,
// identical on every surface. Sized to the preview's close button — the tallest
// thing that can appear in the row — so the label does not shift vertically
// when the user moves between surfaces, which is very visible inline where the
// widget sits in the article flow.
const labelRowClasses = "pw:flex pw:items-center pw:gap-3 pw:w-full pw:h-6";

function Hairline(): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="pw:h-px pw:flex-1 pw:bg-type-muted-foreground"
    />
  );
}

export function AdvertisementLabel({
  className = "",
  trailing,
}: AdvertisementLabelProps): JSX.Element {
  const m = messages();

  return (
    <div className={`${labelRowClasses} ${className}`}>
      <Hairline />
      <span className={labelTextClasses}>{m.advertisement.label}</span>
      <Hairline />
      {trailing}
    </div>
  );
}
